/**
 * The BlytzCollect analytics page's URL: its period, merchant, batch and DPD
 * scheme.
 *
 * The page keeps every choice in the address bar, so a reload or a pasted
 * link opens the same view, and Alert me reads the view from the URL. `period`,
 * `weekStart`, `from` and `to` are spelled as on every other report page.
 * `orgId` and `batchId` are what the endpoint reads. `dpdScheme` changes only
 * a chart, so the saved view does not carry it.
 */

import { calendarDateAtUtcMidnight } from '../../../lib/calendar-date.ts'
import {
  parsePeriodPresetId,
  type PeriodPresetId
} from '../../../lib/period-presets.ts'
import {
  parseWeekStart,
  type WeekStart
} from '../../../lib/reporting-period.ts'
import { DPD_SCHEMES, parseDpdScheme, type DpdSchemeId } from './dpd-buckets.ts'

/**
 * The page's search, as its route validates it.
 *
 * A type rather than an interface, so it is also a `Record<string, unknown>`,
 * which is how the route declares its search.
 */
export type AnalyticsSearch = {
  period?: PeriodPresetId
  weekStart?: WeekStart
  from?: string
  to?: string
  orgId?: number
  batchId?: number
  dpdScheme?: DpdSchemeId
}

/**
 * A positive whole number, from a URL value.
 *
 * @param value - The parsed value: the router reads `287` as a number.
 * @returns The id, or undefined when it is not one.
 */
function positiveId(value: unknown): number | undefined {
  const id =
    typeof value === 'string' && value.trim() !== '' ? Number(value) : value

  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0
    ? id
    : undefined
}

/**
 * A calendar day, from a URL value.
 *
 * @param value - The parsed value.
 * @returns The day as `YYYY-MM-DD`, or undefined when it is not one.
 */
function calendarDay(value: unknown): string | undefined {
  return typeof value === 'string' &&
    Number.isFinite(calendarDateAtUtcMidnight(value))
    ? value
    : undefined
}

/**
 * Read the page's search, and drop what it cannot use.
 *
 * Every key is answered, `undefined` when absent or unusable, so a bad value
 * in the address bar does not reach the page. A batch needs a merchant: the
 * page offers batches only once a merchant narrows them.
 *
 * @param search - The search the router parsed.
 * @returns The page's search.
 */
export function validateAnalyticsSearch(
  search: Record<string, unknown>
): AnalyticsSearch {
  const period = parsePeriodPresetId(search.period) ?? undefined
  const weekStart = parseWeekStart(search.weekStart) ?? undefined
  const orgId = positiveId(search.orgId)
  const scheme = parseDpdScheme(search.dpdScheme)

  return {
    period,
    weekStart,
    from: calendarDay(search.from),
    to: calendarDay(search.to),
    orgId,
    batchId: orgId === undefined ? undefined : positiveId(search.batchId),
    dpdScheme: scheme && scheme !== DPD_SCHEMES[0] ? scheme.id : undefined
  }
}

/**
 * The period part of the search as the text view `useReportScope` reads.
 *
 * @param search - The page's search.
 * @returns `period`, `weekStart`, `from` and `to`, each present only when set.
 */
export function periodView(search: AnalyticsSearch): Record<string, string> {
  const view: Record<string, string> = {}
  if (search.period) view.period = search.period
  if (search.weekStart !== undefined) view.weekStart = String(search.weekStart)
  if (search.from) view.from = search.from
  if (search.to) view.to = search.to

  return view
}

/**
 * The search with a new period written in, from the view `useReportScope`
 * writes.
 *
 * A batch belongs to one moment in time, so a new range drops it. Otherwise
 * the reader would see a batch filter the batch list no longer offers.
 *
 * @param search - The page's search now.
 * @param view - The period view to write.
 * @returns The next search.
 */
export function withPeriodView(
  search: AnalyticsSearch,
  view: Readonly<Record<string, string>>
): AnalyticsSearch {
  const next = validateAnalyticsSearch({
    ...search,
    period: view.period,
    weekStart: view.weekStart,
    from: view.from,
    to: view.to
  })
  const moved = next.from !== search.from || next.to !== search.to

  return moved ? { ...next, batchId: undefined } : next
}
